import { IntegratedApp } from '@/constants/apps';
import { OnboardingStatusSchema, OnboardingStepId } from '@/features/onboarding/types/onboarding';
import { BannerState, SessionBanner } from '@/types/banners';
import { AccountServiceClient } from '@/utils/apiClients';
import { z } from 'zod';

const GetAccountSettingsRequestSchema = z.object({
  appUrl: z.string(),
  appName: z.nativeEnum(IntegratedApp),
});

type GetAccountSettingsRequest = z.infer<typeof GetAccountSettingsRequestSchema>;

const UpdateOnboardingStatusRequestSchema = z.object({
  appUrl: z.string(),
  appName: z.nativeEnum(IntegratedApp),
  onboardingStatus: z.record(z.nativeEnum(OnboardingStepId), OnboardingStatusSchema),
});

type UpdateOnboardingStatusRequest = z.infer<typeof UpdateOnboardingStatusRequestSchema>;

const AccountSettingsResponseSchema = z.object({
  onboardingStatuses: z.record(z.nativeEnum(OnboardingStepId), OnboardingStatusSchema),
  bannersStates: z.record(z.string(), z.string()),
});

export type AccountSettingsResponse = z.infer<typeof AccountSettingsResponseSchema>;

type UpdateOnboardingStatusResponse = Pick<AccountSettingsResponse, 'onboardingStatuses'>;
type UpdateBannerStateResponse = Pick<AccountSettingsResponse, 'bannersStates'>;

const UpdateBannerStateRequestSchema = z.object({
  appUrl: z.string(),
  appName: z.nativeEnum(IntegratedApp),
  bannerState: z.record(z.nativeEnum(SessionBanner), z.string()),
});

type UpdateBannerStateRequest = z.infer<typeof UpdateBannerStateRequestSchema>;

export const getAccountSettings = async (request: GetAccountSettingsRequest): Promise<AccountSettingsResponse> => {
  return await AccountServiceClient.get<AccountSettingsResponse>('/account/settings-by-app-url', {
    appUrl: request.appUrl,
    appName: request.appName,
  });
};

export const getBannersStates = async (
  request: GetAccountSettingsRequest
): Promise<Record<SessionBanner, BannerState>> => {
  return await getAccountSettings({
    appUrl: request.appUrl,
    appName: request.appName,
  }).then((res) => (res.bannersStates || {}) as Record<SessionBanner, BannerState>);
};

export const updateOnboardingStatus = async (
  request: UpdateOnboardingStatusRequest
): Promise<UpdateOnboardingStatusResponse> => {
  return await AccountServiceClient.patch<UpdateOnboardingStatusResponse>('/account/update-onboarding-status', {
    appUrl: request.appUrl,
    appName: request.appName,
    onboardingStatus: request.onboardingStatus,
  });
};

export const updateBannerState = async (request: UpdateBannerStateRequest): Promise<UpdateBannerStateResponse> => {
  return await AccountServiceClient.patch<UpdateBannerStateResponse>('/account/update-banner-state', {
    appUrl: request.appUrl,
    appName: request.appName,
    bannerState: request.bannerState,
  });
};
